/**
 * The shortcuts in use: the config's `keybinds` (the default config's until it arrives), then
 * the keys the services offer beside them, such as a board tool's key, which comes with the
 * tool's node kind (`NodeKindDef.board.tool.key`). An offered key is kept only while the config
 * binds neither that key nor that command, so the config always wins.
 */
import { createMemo, type Accessor } from "solid-js";
import type { Keybind } from "@protocol/Keybind";

/** The keys bound to `command` (with `argument`) in `binds`, as written in the config (`Ctrl+Z`), or `""`. */
export function shortcutOf(binds: readonly Keybind[], command: string | undefined, argument: string | null = null): string {
  if (!command) return "";
  return binds.find((b) => b.action === command && (b.argument ?? null) === argument)?.keys ?? "";
}

/** `configured`, then those of `offered` binding a key and a command neither `configured` nor an earlier offered one binds. */
export function bindsWith(configured: readonly Keybind[], offered: readonly Keybind[]): Keybind[] {
  const keys = new Set(configured.map((b) => b.keys));
  const commands = new Set(configured.map((b) => `${b.action} ${b.argument ?? ""}`));
  const all = [...configured];
  for (const bind of offered) {
    const command = `${bind.action} ${bind.argument ?? ""}`;
    if (!bind.keys || keys.has(bind.keys) || commands.has(command)) continue;
    keys.add(bind.keys);
    commands.add(command);
    all.push(bind);
  }
  return all;
}

/** The shortcuts service. */
export interface KeysService {
  /** Every shortcut in use, the config's first. */
  binds: Accessor<Keybind[]>;
  /** The keys running `command` (with `argument`), or `""`. */
  shortcut(command: string | undefined, argument?: string | null): string;
}

/** What the service is built from. */
export interface KeysDeps {
  /** The config's binds (the default config's until it arrives). */
  configured: Accessor<readonly Keybind[]>;
  /** The keys the services offer (the board's tools). */
  offered: Accessor<readonly Keybind[]>;
}

/** Creates the shortcuts service. */
export function createKeys({ configured, offered }: KeysDeps): KeysService {
  const binds = createMemo(() => bindsWith(configured(), offered()));
  return { binds, shortcut: (command, argument = null) => shortcutOf(binds(), command, argument) };
}
